import React from 'react';
import { Button, DatePicker, Spin, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import LookupField from '../../components/LookupField';
import { usePW0308Report } from './hooks/usePW0308Report';
import type { PW0308ReportRow } from '../../services/pw0308Api';
import './PW0308Form.css';

const columns: ColumnsType<PW0308ReportRow> = [
  {
    title: '使用者代號',
    dataIndex: 'f008_01',
    key: 'f008_01',
    width: 110,
    sorter: (a, b) => a.f008_01.localeCompare(b.f008_01),
  },
  {
    title: '使用日期',
    dataIndex: 'f008_02',
    key: 'f008_02',
    width: 110,
    render: (v: string) => v || '-',
    sorter: (a, b) => (a.f008_02 || '').localeCompare(b.f008_02 || ''),
  },
  {
    title: '程式代號',
    dataIndex: 'f008_03',
    key: 'f008_03',
    width: 100,
  },
  {
    title: '程式名稱',
    dataIndex: 'f008_10',
    key: 'f008_10',
    width: 180,
    sorter: (a, b) => a.f008_10.localeCompare(b.f008_10),
  },
  {
    title: '登入時間',
    dataIndex: 'f008_11',
    key: 'f008_11',
    width: 100,
  },
  {
    title: '登出時間',
    dataIndex: 'f008_12',
    key: 'f008_12',
    width: 100,
  },
  {
    title: '備註',
    dataIndex: 'f008_13',
    key: 'f008_13',
    ellipsis: true,
  },
  {
    title: '其他資訊',
    dataIndex: 'f008_14',
    key: 'f008_14',
    width: 100,
  },
];

const PW0308Form: React.FC = () => {
  const {
    loading, records, total,
    startUser, setStartUser,
    endUser, setEndUser,
    startDate, setStartDate,
    endDate, setEndDate,
    handleQuery,
    handleStartUserConfirm,
  } = usePW0308Report();

  return (
    <Spin spinning={loading}>
      <div className="pw0308-form">
        <h2 style={{ marginBottom: 16 }}>
          系統使用記錄 — pw0308f1
          {total > 0 && (
            <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 400, color: '#1890ff' }}>
              共 {total} 筆
            </span>
          )}
        </h2>

        {/* Filter: User code range (BR-F06/F07: PW9101F6 employee lookup) */}
        <div className="filter-row">
          <span className="filter-label">人員代號：[從]</span>
          <LookupField
            lookupType="employee"
            value={startUser}
            onChange={setStartUser}
            onCodeConfirm={handleStartUserConfirm}
            style={{ width: 140 }}
            maxLength={8}
          />
          <span className="filter-sep">[至]</span>
          <LookupField
            lookupType="employee"
            value={endUser}
            onChange={setEndUser}
            onCodeConfirm={(code) => setEndUser(code)}
            style={{ width: 140 }}
            maxLength={8}
          />
        </div>

        {/* Filter: Date range */}
        <div className="filter-row">
          <span className="filter-label">日期：[從]</span>
          <DatePicker
            placeholder="起始日"
            value={startDate ? dayjs(startDate) : null}
            onChange={(d) => setStartDate(d ? d.format('YYYY-MM-DD') : '')}
            style={{ width: 140 }}
          />
          <span className="filter-sep">[至]</span>
          <DatePicker
            placeholder="結束日"
            value={endDate ? dayjs(endDate) : null}
            onChange={(d) => setEndDate(d ? d.format('YYYY-MM-DD') : '')}
            style={{ width: 140 }}
          />
        </div>

        {/* Query button */}
        <div className="action-row">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleQuery}
            loading={loading}
          >
            開始查詢
          </Button>
        </div>

        {/* Results table */}
        <Table<PW0308ReportRow>
          columns={columns}
          dataSource={records}
          rowKey={(r, idx) => `${r.f008_01}-${r.f008_02}-${idx}`}
          pagination={{
            pageSize: 100,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 筆`,
          }}
          size="small"
          scroll={{ x: 1000 }}
          locale={{ emptyText: '請設定篩選條件後按「開始查詢」' }}
        />
      </div>
    </Spin>
  );
};

export default PW0308Form;
